"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowLeft, Mail, ChevronRight } from "lucide-react";
import { FacultyMember } from "@/data/faculty";

// ── Helpers ───────────────────────────────────────────────────────────────
function extractEmail(html: string): string | null {
  const match = html.match(/mailto:([^"'\s>]+)/i);
  return match ? match[1] : null;
}

// ── Decorative sub-components ─────────────────────────────────────────────
function LeafOrnament() {
  return (
    <svg
      viewBox="0 0 64 16"
      className="h-4 w-16 text-healing-400"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M0 8 H22" stroke="currentColor" strokeWidth="1" fill="none" />
      <path d="M32 4 C28 6 26 8 32 12 C36 8 36 6 32 4 Z" />
      <path d="M42 8 H64" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  );
}

function BotanicalPattern() {
  return (
    <svg
      className="absolute inset-0 h-full w-full opacity-[0.04]"
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="botanical-faculty"
          x="0"
          y="0"
          width="80"
          height="80"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M40 10 C25 25 25 45 40 60 C55 45 55 25 40 10 Z M40 10 V60"
            fill="none"
            stroke="hsl(142 72% 29%)"
            strokeWidth="1"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#botanical-faculty)" />
    </svg>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────
interface Props {
  member: FacultyMember;
  related: FacultyMember[];
}

// ── Main component ────────────────────────────────────────────────────────
export function FacultyDetailClient({ member, related }: Props) {
  const [showBack, setShowBack] = useState(false);
  const email = extractEmail(member.bio);

  useEffect(() => {
    const onScroll = () => setShowBack(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to top on member change (client-side navigation)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [member.slug]);

  return (
    <div className="min-h-screen bg-[hsl(40,30%,98%)] mt-20 md:mt-10">
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(40,40%,96%)] via-[hsl(40,30%,98%)] to-white" />
        <BotanicalPattern />

        <div className="relative container mx-auto px-4 py-16 md:py-24">
          <Link
            href="/faculty"
            className="inline-flex items-center gap-2 text-sm text-healing-700 hover:text-healing-900 transition-colors mb-10"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Faculty
          </Link>

          <div className="mx-auto max-w-3xl rounded-3xl bg-white/70 backdrop-blur-sm border border-healing-100 shadow-[0_20px_60px_-30px_rgba(20,83,45,0.25)] px-8 py-12 md:px-14 md:py-16 text-center">
            {/* Portrait */}
            <div className="mx-auto mb-8 h-44 w-44 md:h-52 md:w-52 rounded-full p-[6px] bg-healing-200 shadow-2xl">
              <div className="h-full w-full rounded-full p-[4px] bg-white">
                <div className="relative h-full w-full rounded-full overflow-hidden">
                  <Image
                    src={`${member.thumbnail}`}
                    alt={member.name}
                    fill
                    sizes="208px"
                    className="object-cover"
                    priority
                  />
                </div>
              </div>
            </div>

            {/* Name */}
            <h1 className="font-serif-display text-4xl md:text-5xl text-healing-900 leading-tight">
              {member.name}
            </h1>

            {/* Qualification */}
            <p className="mt-4 text-sm tracking-[0.2em] text-healing-600 uppercase font-medium">
              {member.qualification}
            </p>

            {/* Position badge */}
            <div className="mt-6 flex justify-center">
              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-4 py-1 text-sm font-medium">
                {member.position}
              </span>
            </div>

            {/* Ornamental divider */}
            <div className="mt-8 flex items-center justify-center gap-3">
              <span className="h-px w-16 bg-healing-200" />
              <LeafOrnament />
              <span className="h-px w-16 bg-healing-200" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Bio ───────────────────────────────────────────────────────── */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div
          className={[
            "mx-auto max-w-[800px]",
            // Paragraph styling
            "[&_p]:text-gray-700 [&_p]:leading-[1.9] [&_p]:tracking-wide [&_p]:mb-5 [&_p]:text-[1.05rem]",
            // First paragraph lead treatment
            "[&_p:first-of-type]:text-xl [&_p:first-of-type]:text-healing-900 [&_p:first-of-type]:font-medium [&_p:first-of-type]:leading-relaxed [&_p:first-of-type]:mb-8",
            // Strong
            "[&_strong]:text-healing-800 [&_strong]:font-semibold",
            // Links
            "[&_a]:text-healing-700 [&_a]:underline [&_a]:decoration-healing-300 [&_a]:underline-offset-2 hover:[&_a]:text-healing-900",
            // Hide &nbsp; spacer paragraphs
            "[&_p:empty]:hidden",
          ].join(" ")}
          dangerouslySetInnerHTML={{ __html: member.bio }}
        />

        {/* ── Email contact card ─────────────────────────────────────── */}
        {email && (
          <div className="mx-auto max-w-[800px] mt-12">
            <a
              href={`mailto:${email}`}
              aria-label={`Email ${email}`}
              className="block rounded-2xl border border-healing-200 bg-gradient-to-br from-healing-50 via-white to-healing-100/60 p-8 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="flex items-start gap-5">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-healing-600 text-white shadow-md group-hover:scale-105 transition-transform duration-300">
                  <Mail className="h-6 w-6" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-widest text-healing-600 font-semibold">
                    Get in touch
                  </p>
                  <p className="mt-1 font-serif-display text-xl md:text-2xl text-healing-900 break-all">
                    {email}
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    Reach out directly for academic enquiries, collaborations,
                    or media requests.
                  </p>
                </div>
              </div>
            </a>
          </div>
        )}
      </section>

      {/* ── Related faculty ───────────────────────────────────────────── */}
      {related.length > 0 && (
        <section className="border-t border-healing-100 bg-[hsl(80,25%,97%)] py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-serif-display text-3xl md:text-4xl text-healing-900">
                Meet the Rest of Our Faculty
              </h2>
              <div className="mt-4 flex items-center justify-center gap-3">
                <span className="h-px w-12 bg-healing-200" />
                <LeafOrnament />
                <span className="h-px w-12 bg-healing-200" />
              </div>
            </div>

            <div className="flex gap-6 overflow-x-auto pb-4 snap-x md:grid md:grid-cols-3 md:overflow-visible">
              {related.map((m) => (
                <Link
                  key={m.id}
                  href={`/faculty-members/${m.slug}`}
                  className="snap-start min-w-[260px] md:min-w-0 group rounded-2xl bg-white border border-healing-100 p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  {/* Thumbnail */}
                  <div className="mx-auto h-24 w-24 rounded-full p-[3px] bg-healing-200">
                    <div className="h-full w-full rounded-full p-[2px] bg-white">
                      <div className="relative h-full w-full rounded-full overflow-hidden">
                        <Image
                          src={`${m.thumbnail}`}
                          alt={m.name}
                          fill
                          sizes="96px"
                          className="object-cover"
                        />
                      </div>
                    </div>
                  </div>

                  <h3 className="mt-4 font-serif-display text-lg text-healing-900">
                    {m.name}
                  </h3>
                  <span className="mt-2 inline-block rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-3 py-0.5 text-xs">
                    {m.position}
                  </span>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm text-healing-700 group-hover:text-healing-900 font-medium">
                    View Profile
                    <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
